import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { prop } from 'ramda';
import { useIsAdmin } from 'js/hooks/user';
import { useTranslations } from 'js/hooks/i18n';
import { Loader } from 'js/components/common/Loader';
import { Portlet } from 'js/components/system/Portlet';
import { ErrorMessage } from 'js/components/common/ErrorMessage';
import { useDeviceSelector, useUserSelector } from 'js/store/hooks';
import { usePageInfoFetch } from 'js/components/system/Page/hocs/withCommonPage/hooks';

import * as actions from '../../../../store/actions';
import { SidebarContext } from '../../../../store/context';

import style from './PortalPage.scss';

export const PortalPage = ({ portalPageURL }) => {
    const dispatch = useDispatch();
    const translate = useTranslations();
    const portalPageContentRef = useRef();
    const formFactor = useDeviceSelector(prop('formFactor'));
    const role = useUserSelector(prop('role'));
    const [{ isLoading, pageError, page }] = usePageInfoFetch({ path: portalPageURL, isPageInPopup: true });
    const { error, is404Error, is403Error } = pageError || {};
    const { portlets, name } = page || {};
    const isAdmin = useIsAdmin();

    useEffect(() => {
        if (error || is404Error || is403Error) {
            dispatch(actions.changeName(''));
        }

        if (name) {
            dispatch(actions.changeName(name));
        }

        portalPageContentRef.current?.focus();
    }, [name, dispatch, error, is404Error, is403Error]);

    if (is403Error) {
        return (
            <ErrorMessage
                animate={true}
                className={style.error}
                message={translate('playtech.mobile.system.myAccountSidebar.page.error.403')}
            />
        );
    }

    if (error || is404Error) {
        const errorMessage = typeof error === 'string' || error instanceof window.Error ? error.toString() : '';

        return (
            <ErrorMessage
                animate={true}
                message={errorMessage}
                className={style.error}
            />
        );
    }

    if (isLoading || !page) {
        return <Loader />;
    }

    return (
        <SidebarContext.Provider value={{ isInSidebar: true }}>
            <span tabIndex={-1} ref={portalPageContentRef} aria-hidden={true} />
            <div className={'emulate-mobile sidebar-content'}>
                {
                    portlets
                        .filter(portlet => isAdmin
                            || Boolean(portlet.enabledDevices[formFactor]) && Boolean(portlet.permissions[role]))
                        .map(portlet => <Portlet key={portlet.portletKey} portlet={portlet}/>)
                }
            </div>
        </SidebarContext.Provider>
    );
};
